import { useEffect } from 'react';
import { useAuth } from '@/providers/authProvider';
import { useImpersonation } from '@/providers/impersonationProvider';
import { useUser } from '@/providers/userProvider';
import { API_BASE_URL } from '@/services/api/baseUrl';
import {
  cardForClick,
  flushEngagement,
  setEngagementCustomer,
  setEngagementSender,
  track,
  watchCardViews,
} from '@/lib/engagement';
import { router } from '@/router';

const SESSION_KEY = 'engagementSessionFor';

// The route pattern for the current page ("/customers/:customerId"), so a
// page view never stores ids or the reset token from a URL.
const currentRoute = () => {
  const matches = router.state.matches;
  const leaf = matches[matches.length - 1];
  return leaf?.route.path ?? router.state.location.pathname;
};

const routeCustomerId = () =>
  router.state.matches.reduce<string | null>(
    (found, m) =>
      found ??
      (m.params.customerId && m.params.customerId !== 'manage'
        ? m.params.customerId
        : null),
    null
  );

/**
 * Records session starts, page views, cards seen and card clicks for the
 * Admin Engagement page. Renders nothing.
 */
export const EngagementTracker = () => {
  const { isAuthenticated, getAccessToken } = useAuth();
  const { impersonateUserId } = useImpersonation();
  const { user, customerId } = useUser();
  const userId = user?.id;
  const enabled = isAuthenticated && !!userId;

  useEffect(() => {
    if (!enabled) {
      setEngagementSender(null);
      return;
    }
    setEngagementSender(async (events) => {
      const token = await getAccessToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
      if (impersonateUserId)
        headers['X-Impersonate-User-Id'] = impersonateUserId;
      // keepalive lets the last batch finish when the tab is closing.
      return fetch(`${API_BASE_URL}/engagement/events/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ events }),
        keepalive: true,
      });
    });
    return () => {
      flushEngagement();
      setEngagementSender(null);
    };
  }, [enabled, getAccessToken, impersonateUserId]);

  useEffect(() => {
    if (!enabled || !userId) return;

    // One session per browser tab per person: a refresh-token sign-in lasts
    // 30 days, so "opened the dashboard" is the honest visit count.
    try {
      if (sessionStorage.getItem(SESSION_KEY) !== userId) {
        sessionStorage.setItem(SESSION_KEY, userId);
        track('session_start');
      }
    } catch {
      track('session_start');
    }

    const onRoute = () => {
      setEngagementCustomer(routeCustomerId(), customerId ?? null);
      track('page_view', currentRoute());
    };
    let lastPath = router.state.location.pathname;
    onRoute();
    const unsubscribe = router.subscribe((state) => {
      if (state.location.pathname === lastPath) return;
      lastPath = state.location.pathname;
      onRoute();
    });

    const onClick = (e: MouseEvent) => {
      const name = cardForClick(e.target);
      if (name) track('card_click', name);
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') flushEngagement();
    };
    document.addEventListener('click', onClick, true);
    document.addEventListener('visibilitychange', onHide);
    const stopCardViews = watchCardViews();
    return () => {
      stopCardViews();
      unsubscribe();
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [enabled, userId, customerId]);

  return null;
};
